import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, Loader2, X, CheckCircle2, Sparkles, FileText, ArrowLeft, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * SaveWithEmailModal - Email registration with Beta NDA
 * Step 1: Enter email
 * Step 2: Review and accept Beta NDA (for new users only)
 */
const SaveWithEmailModal = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  title = "Save Your Work",
  description = "Enter your email to save and access your work anytime"
}) => {
  const { user, setUser } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // NDA state
  const [step, setStep] = useState('email'); // 'email', 'nda', 'success'
  const [ndaContent, setNdaContent] = useState('');
  const [ndaVersion, setNdaVersion] = useState('');
  const [loadingNda, setLoadingNda] = useState(false);

  // If already logged in, just call onSuccess
  useEffect(() => {
    if (user && isOpen) {
      onSuccess?.(user);
      onClose();
    }
  }, [user, isOpen, onSuccess, onClose]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('email');
      setError('');
      setSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Fetch NDA content
  const fetchNda = async () => {
    setLoadingNda(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/legal/beta-nda`);
      setNdaContent(response.data.content);
      setNdaVersion(response.data.version);
    } catch (err) {
      console.error('Failed to load NDA:', err);
      setError('Failed to load agreement. Please try again.');
    } finally {
      setLoadingNda(false);
    }
  };

  // Step 1: Check email and determine if NDA is needed
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    
    try {
      // Try to register without NDA first to check if user exists
      const response = await axios.post(`${BACKEND_URL}/api/auth/quick-register`, {
        email: email.toLowerCase().trim(),
        nda_accepted: false
      }, { withCredentials: true });
      
      if (response.data.success) {
        // Existing user - logged in successfully
        if (response.data.token) {
          localStorage.setItem('if_auth_token', response.data.token);
        }
        if (setUser) {
          setUser(response.data.user);
        }
        setSuccess(true);
        setStep('success');
        
        setTimeout(() => {
          onSuccess?.(response.data.user);
          onClose();
        }, 1500);
      } else if (response.data.requires_nda) {
        // New user - show NDA
        await fetchNda();
        setStep('nda');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to continue. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Accept NDA and complete registration
  const handleAcceptNda = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/quick-register`, {
        email: email.toLowerCase().trim(),
        nda_accepted: true
      }, { withCredentials: true });
      
      if (response.data.success) {
        if (response.data.token) {
          localStorage.setItem('if_auth_token', response.data.token);
        }
        if (setUser) {
          setUser(response.data.user);
        }
        setSuccess(true);
        setStep('success');
        
        setTimeout(() => {
          onSuccess?.(response.data.user);
          onClose();
        }, 1500);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to complete registration. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    localStorage.setItem('redirect_after_auth', window.location.pathname);
    const redirectUrl = `${window.location.origin}/auth`;
    window.location.href = `https://emergentauthagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Email entry step
  const renderEmailStep = () => (
    <>
      <CardHeader className="text-center pb-2">
        <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center mx-auto mb-3">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-xl">{title}</CardTitle>
        <p className="text-sm text-slate-600 mt-1">{description}</p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="save-email">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="save-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                disabled={loading}
                autoFocus
              />
            </div>
            <p className="text-xs text-slate-500">No password needed - just your email!</p>
          </div>
          
          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Checking...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </form>
        
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-slate-500">or</span>
          </div>
        </div>
        
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>
      </CardContent>
    </>
  );

  // NDA review step
  const renderNdaStep = () => (
    <>
      <CardHeader className="text-center pb-2">
        <button 
          onClick={() => setStep('email')}
          className="absolute top-4 left-4 p-1 hover:bg-slate-100 rounded-full"
        >
          <ArrowLeft className="w-5 h-5 text-slate-500" />
        </button>
        <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center mx-auto mb-3">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-xl">Beta Tester Agreement</CardTitle>
        <p className="text-sm text-slate-600 mt-1">
          Please review and accept our Beta NDA to continue
        </p>
        <div className="flex items-center justify-center gap-2 mt-2 text-xs text-slate-500">
          <FileText className="w-3 h-3" />
          <span>Version {ndaVersion}</span>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
        
        {loadingNda ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            <span className="ml-2 text-slate-600">Loading agreement...</span>
          </div>
        ) : (
          <>
            {/* NDA Content - Scrollable */}
            <div className="max-h-[300px] overflow-y-auto border border-slate-200 rounded-lg p-4 bg-slate-50 text-sm">
              <div className="prose prose-sm prose-slate max-w-none">
                <ReactMarkdown
                  components={{
                    h1: ({children}) => <h1 className="text-lg font-bold text-purple-900 mb-4">{children}</h1>,
                    h2: ({children}) => <h2 className="text-base font-semibold text-slate-800 mt-4 mb-2">{children}</h2>,
                    h3: ({children}) => <h3 className="text-sm font-medium text-slate-700 mt-3 mb-1">{children}</h3>,
                    p: ({children}) => <p className="text-slate-600 mb-2 leading-relaxed">{children}</p>,
                    ul: ({children}) => <ul className="list-disc pl-5 mb-2 text-slate-600">{children}</ul>,
                    li: ({children}) => <li className="mb-1">{children}</li>,
                    strong: ({children}) => <strong className="font-semibold text-slate-800">{children}</strong>,
                    hr: () => <hr className="my-4 border-slate-200" />,
                  }}
                >
                  {ndaContent}
                </ReactMarkdown>
              </div>
            </div>
            
            {/* Email confirmation */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-800">
                <span className="font-medium">Registering as:</span> {email}
              </p>
            </div>
            
            {/* Accept button */}
            <Button 
              onClick={handleAcceptNda}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 py-6"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Creating account...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  I Accept - Create My Account
                </>
              )}
            </Button>
            
            <p className="text-xs text-center text-slate-500">
              By clicking "I Accept", you agree to the terms of this Beta NDA
            </p>
          </>
        )}
      </CardContent>
    </>
  );

  // Success step
  const renderSuccessStep = () => (
    <>
      <CardHeader className="text-center pb-2">
        <div className="w-16 h-16 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-xl">Welcome to the Beta!</CardTitle>
        <p className="text-sm text-slate-600 mt-1">
          Your account has been created successfully
        </p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="text-center py-4">
          <p className="text-green-600 font-medium">✓ Account created successfully!</p>
          <p className="text-sm text-slate-500 mt-2">Redirecting...</p>
        </div>
      </CardContent>
    </>
  );

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && step !== 'nda' && onClose()}
    >
      <Card className={`w-full relative animate-in fade-in zoom-in duration-200 ${step === 'nda' ? 'max-w-2xl' : 'max-w-md'}`}>
        {step !== 'nda' && (
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-1 hover:bg-slate-100 rounded-full z-10"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        )}
        
        {step === 'email' && renderEmailStep()}
        {step === 'nda' && renderNdaStep()}
        {step === 'success' && renderSuccessStep()}
      </Card>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SaveWithEmailModal;
